// Reports: every report as Excel, PDF or CSV, built on the server for the
// period and filters chosen at the top of the page.
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { BarChart3, Building2, CalendarDays, FileSpreadsheet, FileText, Layers, MapPin, Medal, Sheet, UserRound, Users, Wallet } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { api } from '@/shared/api/axios';
import { errText, monthName, type OverviewFilters } from '../api';

const REPORTS: Array<{ kind: string; title: string; text: string; icon: LucideIcon; all?: boolean; period?: boolean }> = [
  { kind: 'performance', title: 'Employee performance', text: 'Every salesperson: target, sales, new vs existing, conversions, achievement, score, grade, incentive.', icon: UserRound, period: true },
  { kind: 'ranking', title: 'Employee ranking', text: 'The leaderboard for the chosen period, ranked by overall score.', icon: Medal, period: true },
  { kind: 'incentives', title: 'Incentives & payouts', text: 'Each incentive with its breakdown, approval status, payment date and transaction reference.', icon: Wallet },
  { kind: 'monthly', title: 'Monthly sales', text: 'Month by month for the financial year: sales, new / existing, conversions, target, incentive.', icon: CalendarDays },
  { kind: 'quarterly', title: 'Quarterly performance', text: 'Q1–Q4: target, actual, achievement, new and existing sales, conversions, incentive.', icon: Layers },
  { kind: 'department', title: 'Department performance', text: 'Sales vs Field Executive side by side.', icon: Building2, all: true, period: true },
  { kind: 'territory', title: 'Territory performance', text: 'Every territory compared.', icon: MapPin, all: true, period: true },
  { kind: 'branch', title: 'Branch performance', text: 'Every branch compared.', icon: BarChart3, all: true, period: true },
  { kind: 'manager', title: 'Manager (team) performance', text: 'Each manager\'s team compared.', icon: Users, all: true, period: true },
];

const FORMATS = [
  { ext: 'xlsx', label: 'Excel', icon: FileSpreadsheet, cls: 'text-emerald-700 hover:bg-emerald-50 border-emerald-200' },
  { ext: 'pdf', label: 'PDF', icon: FileText, cls: 'text-rose-700 hover:bg-rose-50 border-rose-200' },
  { ext: 'csv', label: 'CSV', icon: Sheet, cls: 'text-slate-700 hover:bg-slate-50 border-slate-200' },
] as const;

export function ReportsTab({ filters, seesAll }: { filters: OverviewFilters; seesAll: boolean }) {
  const [busy, setBusy] = useState('');
  const period = filters.view === 'month' ? monthName(filters.month || '') : filters.view === 'quarter' ? `${filters.quarter} FY ${filters.fy}` : `FY ${filters.fy}`;
  const download = async (kind: string, ext: string) => {
    const id = `${kind}.${ext}`;
    setBusy(id);
    try {
      const p = new URLSearchParams(Object.entries({ ...filters, kind, month: kind === 'incentives' && filters.view !== 'month' ? '' : filters.month }).filter(([, v]) => v) as [string, string][]);
      const res = await api.get(`/api/sales-performance/export.${ext}?${p}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a'); a.href = url; a.download = `${kind}-${(period || filters.fy).replace(/\s+/g, '-')}.${ext}`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (err) {
      let msg = 'Could not create the report.';
      const blob = (err as { response?: { data?: Blob } })?.response?.data;
      if (blob instanceof Blob) { try { msg = JSON.parse(await blob.text()).error || msg; } catch { /* keep default */ } } else msg = errText(err, msg);
      toast.error(msg);
    } finally { setBusy(''); }
  };
  return (
    <div className="flex flex-col gap-4">
      <p className="rounded-xl bg-blue-50 px-4 py-2.5 text-[13px] text-blue-800">
        Reports use the period and filters chosen above — now <b>{period}</b>{[filters.department, filters.territory, filters.branch].filter(Boolean).map((x) => ` · ${x}`).join('')}{filters.manager ? ' · one team' : ''}{filters.employee ? ' · one employee' : ''}.
      </p>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {REPORTS.filter((r) => !r.all || seesAll).map((r) => (
          <div key={r.kind} className="flex flex-col rounded-2xl border border-slate-200/80 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600"><r.icon className="h-5 w-5" /></span>
              <div>
                <h3 className="text-[15px] font-semibold text-slate-900">{r.title}</h3>
                <p className="mt-1 text-xs leading-relaxed text-slate-500">{r.text}</p>
                <p className="mt-1.5 text-data-xs font-medium text-slate-400">{r.period ? period : `FY ${filters.fy}`}</p>
              </div>
            </div>
            <div className="mt-4 flex gap-2 pt-1">
              {FORMATS.map((fm) => (
                <button key={fm.ext} type="button" disabled={!!busy} onClick={() => download(r.kind, fm.ext)}
                  className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-2 py-1.5 text-xs font-semibold transition disabled:opacity-50 ${fm.cls}`}>
                  <fm.icon className="h-3.5 w-3.5" /> {busy === `${r.kind}.${fm.ext}` ? '…' : fm.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
